
export type Primitive = string | number | boolean;
export function isPrimitive(input: unknown): input is Primitive
{
    return input !== undefined
        && input !== null
        && !Array.isArray(input)
        && (typeof input === "string"
            || typeof input === "number"
            || typeof input === "boolean"
            || input instanceof Boolean
            || input instanceof Date
            || input instanceof Number
            || input instanceof RegExp
            || input instanceof String);
}

export function removeUndefinedFields<T = unknown>(source: T): T
{
    if (isPrimitive(source))
    {
        return source;
    }

    if (Array.isArray(source))
    {
        return source.map(x => removeUndefinedFields(x)) as T;
    }

    if (typeof source === "object")
    {
        return Object.entries(source as object)
            .reduce(
                (o: Record<string, unknown>, [key, value]: [string, unknown]): Record<string, unknown> =>
                {
                    if (value === undefined)
                    {
                        return o;
                    }

                    if (isPrimitive(value))
                    {
                        o[key] = value;
                        return o;
                    }

                    if (Array.isArray(value))
                    {
                        o[key] = value.map(x => removeUndefinedFields(x));
                        return o;
                    }

                    if (typeof value === "object")
                    {
                        o[key] = removeUndefinedFields(value);
                        return o;
                    }

                    o[key] = value;
                    return o;
                },
                {}) as T;
    }

    throw new Error(`hit unexpected edge case: typeof source: ${typeof source}`);
}

/**
 * This will create a deep copy of the given object, including functions.
 * @param source
 * @returns
 */
export function deepCopy<T>(source: T): T
{
    if (source === undefined
        || source === null
        || isPrimitive(source))
    {
        return source;
    }

    if (Array.isArray(source))
    {
        return source.map(x => deepCopy(x)) as T;
    }

    if (typeof source === "object")
    {
        return Object.entries(source as object)
            .reduce(
                (o: Record<string, unknown>, [k, v]: [string, unknown]) =>
                {
                    if (typeof v === "function")
                    {
                        // TODO - assuming we don't have to bind the function to `o`?
                        o[k] = v;
                    }
                    else
                    {
                        o[k] = deepCopy(v);
                    }
                    return o;
                },
                {
                }) as T;
    }

    throw new Error(`hit unexpected edge case: typeof source: ${typeof source}`);
}

const ObjectHelpers = {
    isPrimitive,
    removeUndefinedFields,
    deepCopy,
};

export default ObjectHelpers;
